import { Box, Button, Flex, HStack, IconButton, Text } from '@chakra-ui/react'
import { FilterCondition, FilterOperator, FilterValueType } from '@helo/cms'
import { addMonths, format, isAfter, isValid, subMonths } from 'date-fns'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLilius } from 'use-lilius'

import { FilterCriteria } from '../../services/queryService'
import { formatUTCDate, parseUTCDateString } from '../../utils/dates'
import { defaultFilterCriteria } from '../FilterEditor/dateFilterInputAdapters'
import { ChevronIcon } from '../Icons/ChevronIcon'
import { Icon } from '../Icons/Icon'
import { PopoverCard } from '../PopoverCard/PopoverCard'
import { YearMonthSelector } from '../YearMonthSelector/YearMonthSelector'
import { dateToIso } from '../inputs/DateInput'
import {
  RangeDateInput,
  RangeDateInputRefs,
} from '../inputs/RangeDateInput/RangeDateInput'
import { Divider } from './Calendar-parts'
import { RangeSelectCalendar } from './RangeSelectCalendar'

export interface RangeCalendarProps {
  filterCriteria?: FilterCriteria
  columnId?: string
  onUpdate?: (criteria: FilterCriteria) => void
  autoFocus?: boolean
  /** Click-outside boundary ID so selecting a date doesn't close a parent popover (e.g. filter editor). */
  clickBoundaryId?: string
  /** When set, calendar renders into this container (e.g. chip popover) so clicks stay "inside". */
  containerRef?: React.RefObject<HTMLElement | null>
}

export const RangeCalendar = ({
  onUpdate = () => {},
  filterCriteria = defaultFilterCriteria,
  columnId,
  autoFocus,
  clickBoundaryId,
  containerRef,
}: RangeCalendarProps) => {
  const lilus = useLilius({ numberOfMonths: 2 })
  const {
    selected,
    setViewing,
    viewing,
    viewNextMonth,
    viewPreviousMonth,
    setSelected,
    selectRange,
  } = lilus

  const [isCalOpen, setIsCalOpen] = useState(false)
  const [isEnabledUseOutsideClick, setIsEnabledUseOutsideClick] =
    useState<boolean>(true)
  const inputContainerRef = useRef<HTMLInputElement>(null)
  const calContainerRef = useRef<HTMLInputElement>(null)
  const calPopoverCardRef = useRef<HTMLDivElement | null>(null)
  const rangeDateInputRef = useRef<RangeDateInputRefs>(null)
  const [errorMsg, setErrorMsg] = useState<string>('')
  const [applyIsDisabled, setApplyIsDisabled] = useState<boolean>(true)

  const filterValue = filterCriteria?.filterValue as string | undefined
  const filterToValue = filterCriteria?.filterToValue as string | undefined
  useEffect(() => {
    const fromInputRef = rangeDateInputRef.current?.fromInputRef
    const toInputRef = rangeDateInputRef.current?.toInputRef

    if (filterValue && filterToValue) {
      // Set inputs from saved filter values
      const fromDateFormatted = parseUTCDateString(filterValue)
      const toDateFormatted = parseUTCDateString(filterToValue)

      if (fromDateFormatted && toDateFormatted) {
        if (fromInputRef)
          fromInputRef.value = formatUTCDate(fromDateFormatted, 'MM-dd-yyyy')
        if (toInputRef)
          toInputRef.value = formatUTCDate(toDateFormatted, 'MM-dd-yyyy')
        selectRange(fromDateFormatted, toDateFormatted)
      }
    } else if (!filterValue && !filterToValue) {
      // Clear inputs only if BOTH values are empty (filter was explicitly cleared)
      // This prevents clearing during transitional states like:
      // - Switching to range mode (old date filter might have filterValue but no filterToValue)
      // - Waiting for parent updates after Apply
      setSelected([])
      if (fromInputRef) fromInputRef.value = ''
      if (toInputRef) toInputRef.value = ''
    }
    // If only one value exists, keep current input state (transitional state)
  }, [filterValue, filterToValue, selectRange, setSelected])

  const syncValueAndClose = () => {
    if (!isCalOpen) return
    const fromInputRef = rangeDateInputRef.current?.fromInputRef
    const toInputRef = rangeDateInputRef.current?.toInputRef
    if (selected.length >= 2) {
      const sorted = [...selected].sort((a, b) => a.getTime() - b.getTime())
      const start = sorted[0]
      const end = sorted[sorted.length - 1]
      if (fromInputRef && toInputRef) {
        fromInputRef.value = format(start, 'MM-dd-yyyy')
        toInputRef.value = format(end, 'MM-dd-yyyy')
      }
      setApplyIsDisabled(false)
      onUpdate({
        ...filterCriteria,
        columnId,
        filterValue: dateToIso(start),
        filterToValue: dateToIso(end),
        filterValueType: FilterValueType.date,
        operator: FilterOperator.AND,
        condition: FilterCondition.inRange,
      })
    }
    setIsCalOpen(false)
  }

  const isClickInsideCalendar = useCallback((target: Node | null) => {
    if (!target || !(target instanceof Element)) return false
    // Only treat clicks inside THIS calendar's own popover card as "inside".
    // Matching any [data-popover-card] ancestor would wrongly include the parent
    // filter card, so clicking sibling controls (e.g. the condition dropdown)
    // would fail to close the calendar.
    const insidePopover = calPopoverCardRef.current?.contains(target) ?? false
    const insideCal = calContainerRef.current?.contains(target) ?? false
    const insideInput = inputContainerRef.current?.contains(target) ?? false
    return insidePopover || insideCal || insideInput
  }, [])

  useEffect(() => {
    if (!isCalOpen || !isEnabledUseOutsideClick) return
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (isClickInsideCalendar(target)) return
      syncValueAndClose()
    }
    document.addEventListener('mousedown', onDocMouseDown, true)
    return () => document.removeEventListener('mousedown', onDocMouseDown, true)
  }, [isCalOpen, isEnabledUseOutsideClick, selected, isClickInsideCalendar])

  return (
    <Box
      ref={inputContainerRef}
      alignSelf="stretch"
      minWidth={0}
      onBlur={(e: React.FocusEvent) => {
        const relatedTarget = e.relatedTarget as HTMLElement | null
        if (relatedTarget && inputContainerRef.current?.contains(relatedTarget))
          return
        if (relatedTarget && calContainerRef.current?.contains(relatedTarget))
          return
        if (
          relatedTarget &&
          calPopoverCardRef.current?.contains(relatedTarget)
        ) {
          return
        }
        if (relatedTarget === null) return
        const fromValue = rangeDateInputRef.current?.fromInputRef?.value
        const toValue = rangeDateInputRef.current?.toInputRef?.value
        if (
          fromValue &&
          fromValue.length === 10 &&
          toValue &&
          toValue.length === 10
        ) {
          const [mF, dF, yF] = fromValue.split('-')
          const fromDate = new Date(`${mF} ${dF}, ${yF} 00:00:00`)
          const [mT, dT, yT] = toValue.split('-')
          const toDate = new Date(`${mT} ${dT}, ${yT} 00:00:00`)
          if (
            isValid(fromDate) &&
            isValid(toDate) &&
            isAfter(toDate, fromDate)
          ) {
            onUpdate({
              ...filterCriteria,
              columnId,
              filterValue: dateToIso(fromDate),
              filterToValue: dateToIso(toDate),
              filterValueType: FilterValueType.date,
              operator: FilterOperator.AND,
              condition: FilterCondition.inRange,
            })
          }
        }
        setIsCalOpen(false)
      }}
    >
      <Box
        onClick={() => {
          setIsCalOpen(true)
          setErrorMsg('')
        }}
      >
        <RangeDateInput
          color={errorMsg !== '' ? 'presets.pink.70' : 'none'}
          ref={rangeDateInputRef}
          autoFocus={autoFocus}
          onUpdate={({ to, from }) => {
            const [dFrom, mFrom, yFrom] = from.split('-')
            const convertedFromDate = new Date(
              `${dFrom} ${mFrom}, ${yFrom} 00:00:00`,
            )
            const [dTo, mTo, yTo] = to.split('-')
            const convertedToDate = new Date(`${dTo} ${mTo}, ${yTo} 00:00:00`)
            if (from.length === 10) {
              if (isValid(convertedFromDate)) {
                setViewing(convertedFromDate)
                setSelected([convertedFromDate])
              }
              if (to.length === 10) {
                if (isValid(convertedToDate)) {
                  selectRange(convertedFromDate, convertedToDate, true)
                  if (isAfter(convertedToDate, convertedFromDate)) {
                    onUpdate({
                      ...filterCriteria,
                      columnId,
                      filterValue: dateToIso(convertedFromDate),
                      filterToValue: dateToIso(convertedToDate),
                      filterValueType: FilterValueType.date,
                      operator: FilterOperator.AND,
                      condition: FilterCondition.inRange,
                    })
                  }
                }
              }
            }
            setApplyIsDisabled(to.length !== 10 || from.length !== 10)
          }}
          rightInputElement={
            <Icon
              icon="CalendarIcon"
              data-testid="range-date-calendar-toggle"
              boxSize="14px"
              marginRight="-2px"
              cursor="pointer"
              onMouseEnter={() => setIsEnabledUseOutsideClick(false)}
              onMouseLeave={() => setIsEnabledUseOutsideClick(true)}
              onClick={() => {
                setErrorMsg('')
                setTimeout(() => setIsCalOpen(!isCalOpen), 0)
              }}
              color={isCalOpen ? 'presets.turquoise.50' : 'presets.neutral.60'}
            />
          }
        />
      </Box>

      {errorMsg !== '' && (
        <Text fontSize="10px" fontWeight="500" color="presets.pink.70">
          {errorMsg}
        </Text>
      )}
      <PopoverCard
        referenceElement={inputContainerRef.current}
        isOpen={isCalOpen}
        clickBoundaryId={clickBoundaryId}
        onClose={() => {}}
        padding="0px"
        containerRef={containerRef}
        contentRef={calPopoverCardRef}
        data-testid="range-date-calendar-popover"
      >
        <Flex
          padding="8px 16px 0px 16px"
          ref={calContainerRef}
          flexDirection="column"
        >
          <Flex alignItems="center" paddingBottom="8px">
            <Flex
              justifyContent="space-between"
              width="50%"
              alignItems="center"
            >
              <IconButton
                aria-label="previous month"
                fontSize="16px"
                color="presets.neutral.60"
                padding="4px"
                ml="-12px"
                onClick={viewPreviousMonth}
                icon={<ChevronIcon transform="rotate(90deg)" />}
              />
              <YearMonthSelector
                currentMonth={viewing.getMonth()}
                currentYear={viewing.getFullYear()}
                setCalendar={setViewing}
              />
              <ChevronIcon visibility="hidden" />
            </Flex>
            <Flex
              width="50%"
              alignItems="center"
              justifyContent="space-between"
            >
              <ChevronIcon visibility="hidden" />
              <YearMonthSelector
                currentMonth={addMonths(viewing, 1).getMonth()}
                currentYear={addMonths(viewing, 1).getFullYear()}
                setCalendar={date => {
                  setViewing(subMonths(date, 1))
                }}
              />
              <IconButton
                aria-label="next month"
                fontSize="16px"
                color="presets.neutral.60"
                padding="4px"
                mr="-12px"
                onClick={viewNextMonth}
                icon={<ChevronIcon transform="rotate(270deg)" />}
              />
            </Flex>
          </Flex>
          <Flex w="100%">
            <Divider />
            <Box w="40px" h="1px" />
            <Divider />
          </Flex>
          <RangeSelectCalendar
            lilus={lilus}
            date={selected[0] || new Date()}
            onUpdate={({ toDate, fromDate }) => {
              const fromInputRef = rangeDateInputRef.current?.fromInputRef
              const toInputRef = rangeDateInputRef.current?.toInputRef
              if (!fromDate) {
                if (fromInputRef) fromInputRef.value = ''
                if (toInputRef) toInputRef.value = ''
              }
              if (fromDate) {
                if (fromInputRef) {
                  fromInputRef.value = format(fromDate, 'MM-dd-yyyy')
                  if (toInputRef) toInputRef.value = ''
                }
                if (toDate) {
                  if (toInputRef) {
                    toInputRef.value = format(toDate, 'MM-dd-yyyy')
                  }
                }
              }
              setApplyIsDisabled(
                !(
                  isValid(toDate) &&
                  isValid(fromDate) &&
                  fromDate?.toDateString() !== toDate?.toDateString()
                ),
              )
            }}
          />
          <Box marginLeft="-16px" marginRight="-16px" width="calc(100% + 32px)">
            <Divider />
            <Flex padding="8px 16px">
              <HStack gap="8px" justifyContent="space-between" width="100%">
                <Button
                  size="sm"
                  variant="tertiary"
                  onClick={() => {
                    const fromInputRef = rangeDateInputRef.current?.fromInputRef
                    const toInputRef = rangeDateInputRef.current?.toInputRef
                    setSelected([])
                    if (fromInputRef) fromInputRef.value = ''
                    if (toInputRef) toInputRef.value = ''
                    setApplyIsDisabled(true)
                    onUpdate({
                      columnId,
                      filterToValue: '',
                      filterValue: '',
                      condition: FilterCondition.equals,
                    })
                  }}
                >
                  Clear
                </Button>
                <HStack gap="8px">
                  <Button
                    size="sm"
                    variant="tertiary"
                    onClick={() => {
                      const fromInputRef =
                        rangeDateInputRef.current?.fromInputRef
                      const toInputRef = rangeDateInputRef.current?.toInputRef
                      if (
                        filterCriteria?.filterValue &&
                        filterCriteria?.filterToValue &&
                        fromInputRef &&
                        toInputRef
                      ) {
                        const fromParsed = parseUTCDateString(
                          filterCriteria.filterValue as string,
                        )
                        const toParsed = parseUTCDateString(
                          filterCriteria.filterToValue as string,
                        )
                        if (fromParsed && toParsed) {
                          fromInputRef.value = formatUTCDate(
                            fromParsed,
                            'MM-dd-yyyy',
                          )
                          toInputRef.value = formatUTCDate(
                            toParsed,
                            'MM-dd-yyyy',
                          )
                          selectRange(fromParsed, toParsed, true)
                        }
                      } else if (fromInputRef && toInputRef) {
                        fromInputRef.value = ''
                        toInputRef.value = ''
                        setSelected([])
                      }
                      setApplyIsDisabled(true)
                      onUpdate({ ...filterCriteria, columnId })
                      setTimeout(() => setIsCalOpen(false), 0)
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    isDisabled={applyIsDisabled}
                    onClick={() => {
                      const fromValue =
                        rangeDateInputRef.current?.fromInputRef?.value
                      const toValue =
                        rangeDateInputRef.current?.toInputRef?.value
                      if (
                        fromValue &&
                        fromValue.length === 10 &&
                        toValue &&
                        toValue.length === 10
                      ) {
                        const [dFrom, mFrom, yFrom] = fromValue.split('-')
                        const convertedFromDate = new Date(
                          `${dFrom} ${mFrom}, ${yFrom} 00:00:00`,
                        )

                        const [dTo, mTo, yTo] = toValue.split('-')
                        const convertedToDate = new Date(
                          `${dTo} ${mTo}, ${yTo} 00:00:00`,
                        )

                        if (isAfter(convertedToDate, convertedFromDate)) {
                          const sorted = [...selected].sort(
                            (a, b) => a.getTime() - b.getTime(),
                          )
                          onUpdate({
                            ...filterCriteria,
                            columnId,
                            filterValue: dateToIso(sorted[0]),
                            filterToValue: dateToIso(sorted[sorted.length - 1]),
                            filterValueType: FilterValueType.date,
                            operator: FilterOperator.AND,
                            condition: FilterCondition.inRange,
                          })
                        } else {
                          setErrorMsg('Dates are nonchronological.')
                        }
                      }

                      setTimeout(() => setIsCalOpen(false), 0)
                    }}
                  >
                    Done
                  </Button>
                </HStack>
              </HStack>
            </Flex>
          </Box>
        </Flex>
      </PopoverCard>
    </Box>
  )
}
